import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_BASE_URL}/api`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("token");
    if (token) headers.set("authorization", `Bearer ${token}`);
    return headers;
  },
});

// Wrap the base query so an expired/invalid session clears local auth and
// bounces the user back to the login screen instead of silently failing.
const baseQueryWithReauth = async (args, apiObj, extraOptions) => {
  const result = await rawBaseQuery(args, apiObj, extraOptions);
  if (result.error && result.error.status === 401) {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
  }
  return result;
};

export const api = createApi({
  baseQuery: baseQueryWithReauth,
  reducerPath: "adminApi",
  tagTypes: [
    "User",
    "Products",
    "Customers",
    "Orders",
    "Suppliers",
    "Analytics",
    "Dashboard",
    "Sales",
    "Geography",
    "Transactions",
    "Admins",
    "Categories",
    "Brands",
    "Settings",
    "Refunds",
    "Notifications",
    "Tickets",
    "Conversations",
  ],
  endpoints: (build) => ({
    // ── Auth ──────────────────────────────────────────────────────────────
    getUser: build.query({
      query: () => "auth_routes/me",
      transformResponse: (response) => response?.data ?? response,
      providesTags: ["User"],
    }),

    // ── Products ──────────────────────────────────────────────────────────
    getProducts: build.query({
      query: ({ page = 1, limit = 20, search = "", sort = "" } = {}) => ({
        url: "product_routes",
        params: { page, limit, search, sort },
      }),
      providesTags: ["Products"],
    }),
    getProduct: build.query({
      query: (id) => `product_routes/${id}`,
      providesTags: ["Products"],
    }),
    createProduct: build.mutation({
      query: (body) => ({ url: "product_routes", method: "POST", body }),
      invalidatesTags: ["Products"],
    }),
    updateProduct: build.mutation({
      query: ({ id, ...body }) => ({ url: `product_routes/${id}`, method: "PUT", body }),
      invalidatesTags: ["Products"],
    }),
    deleteProduct: build.mutation({
      query: (id) => ({ url: `product_routes/${id}`, method: "DELETE" }),
      invalidatesTags: ["Products"],
    }),
    getLowStockProducts: build.query({
      query: () => "product_routes/low-stock",
      providesTags: ["Products"],
    }),
    updateStock: build.mutation({
      query: ({ id, ...body }) => ({ url: `product_routes/${id}/stock`, method: "PUT", body }),
      invalidatesTags: ["Products"],
    }),

    // ── Orders ────────────────────────────────────────────────────────────
    getOrders: build.query({
      query: ({ page = 1, limit = 20, status = "", search = "" } = {}) => ({
        url: "order_routes",
        params: { page, limit, status, search },
      }),
      providesTags: ["Orders"],
    }),
    getOrder: build.query({
      query: (id) => `order_routes/${id}`,
      providesTags: ["Orders"],
    }),
    createOrder: build.mutation({
      query: (body) => ({ url: "order_routes", method: "POST", body }),
      invalidatesTags: ["Orders", "Products"],
    }),
    updateOrderStatus: build.mutation({
      query: ({ id, status }) => ({ url: `order_routes/${id}/status`, method: "PUT", body: { status } }),
      invalidatesTags: ["Orders"],
    }),
    processPayment: build.mutation({
      query: ({ id, ...body }) => ({ url: `order_routes/${id}/payment`, method: "PUT", body }),
      invalidatesTags: ["Orders"],
    }),
    cancelOrder: build.mutation({
      query: (id) => ({ url: `order_routes/${id}/cancel`, method: "PUT" }),
      invalidatesTags: ["Orders", "Products"],
    }),

    // ── Customers ─────────────────────────────────────────────────────────
    getCustomers: build.query({
      query: ({ page = 1, limit = 20, search = "" } = {}) => ({
        url: "customer_routes",
        params: { page, limit, search },
      }),
      providesTags: ["Customers"],
    }),
    getCustomer: build.query({
      query: (id) => `customer_routes/${id}`,
      providesTags: ["Customers"],
    }),
    createCustomer: build.mutation({
      query: (body) => ({ url: "customer_routes", method: "POST", body }),
      invalidatesTags: ["Customers"],
    }),
    updateCustomer: build.mutation({
      query: ({ id, ...body }) => ({ url: `customer_routes/${id}`, method: "PUT", body }),
      invalidatesTags: ["Customers"],
    }),
    deleteCustomer: build.mutation({
      query: (id) => ({ url: `customer_routes/${id}`, method: "DELETE" }),
      invalidatesTags: ["Customers"],
    }),

    // ── Suppliers ─────────────────────────────────────────────────────────
    getSuppliers: build.query({
      query: ({ page = 1, limit = 20, search = "" } = {}) => ({
        url: "supplier_routes",
        params: { page, limit, search },
      }),
      providesTags: ["Suppliers"],
    }),
    getSupplier: build.query({
      query: (id) => `supplier_routes/${id}`,
      providesTags: ["Suppliers"],
    }),
    createSupplier: build.mutation({
      query: (body) => ({ url: "supplier_routes", method: "POST", body }),
      invalidatesTags: ["Suppliers"],
    }),
    updateSupplier: build.mutation({
      query: ({ id, ...body }) => ({ url: `supplier_routes/${id}`, method: "PUT", body }),
      invalidatesTags: ["Suppliers"],
    }),
    deleteSupplier: build.mutation({
      query: (id) => ({ url: `supplier_routes/${id}`, method: "DELETE" }),
      invalidatesTags: ["Suppliers"],
    }),

    // ── Analytics / Dashboard ─────────────────────────────────────────────
    getDashboard: build.query({
      query: () => "analytic_routes/dashboard",
      providesTags: ["Dashboard"],
    }),
    getSales: build.query({
      query: ({ startDate = "", endDate = "" } = {}) => ({
        url: "analytic_routes/sales",
        params: { startDate, endDate },
      }),
      providesTags: ["Sales"],
    }),
    getInventoryAnalytics: build.query({
      query: () => "analytic_routes/inventory",
      providesTags: ["Analytics"],
    }),
    getCustomerAnalytics: build.query({
      query: () => "analytic_routes/customers",
      providesTags: ["Analytics"],
    }),
    getGeography: build.query({
      query: () => "analytic_routes/regional",
      providesTags: ["Geography"],
    }),
    getFinancialAnalytics: build.query({
      query: () => "analytic_routes/finance",
      providesTags: ["Analytics"],
    }),
    getCategoryAnalytics: build.query({
      query: () => "analytic_routes/categories",
      providesTags: ["Analytics"],
    }),
    getPaymentAnalytics: build.query({
      query: () => "analytic_routes/payments",
      providesTags: ["Analytics"],
    }),
    getSupplierAnalytics: build.query({
      query: () => "analytic_routes/suppliers",
      providesTags: ["Analytics"],
    }),

    // ── Transactions (orders used as transactions) ────────────────────────
    getTransactions: build.query({
      query: ({ page = 1, pageSize = 20, sort = "", search = "" } = {}) => ({
        url: "order_routes",
        params: { page, limit: pageSize, sort, search },
      }),
      providesTags: ["Transactions"],
    }),

    // ── Admins (users with admin role) ────────────────────────────────────
    getAdmins: build.query({
      query: () => "user_routes?role=admin",
      providesTags: ["Admins"],
    }),
    getUserPerformance: build.query({
      query: () => "analytic_routes/finance",
      providesTags: ["Analytics"],
    }),

    // ── Categories ────────────────────────────────────────────────────────
    getCategories: build.query({
      query: ({ search = "" } = {}) => ({ url: "category_routes", params: { search } }),
      providesTags: ["Categories"],
    }),
    createCategory: build.mutation({
      query: (body) => ({ url: "category_routes", method: "POST", body }),
      invalidatesTags: ["Categories"],
    }),
    updateCategory: build.mutation({
      query: ({ id, ...body }) => ({ url: `category_routes/${id}`, method: "PUT", body }),
      invalidatesTags: ["Categories"],
    }),
    deleteCategory: build.mutation({
      query: (id) => ({ url: `category_routes/${id}`, method: "DELETE" }),
      invalidatesTags: ["Categories"],
    }),
    getSubCategories: build.query({
      query: () => "category_routes/sub/all",
      providesTags: ["Categories"],
    }),
    addSubCategory: build.mutation({
      query: ({ categoryId, ...body }) => ({ url: `category_routes/${categoryId}/sub`, method: "POST", body }),
      invalidatesTags: ["Categories"],
    }),
    deleteSubCategory: build.mutation({
      query: ({ categoryId, subId }) => ({ url: `category_routes/${categoryId}/sub/${subId}`, method: "DELETE" }),
      invalidatesTags: ["Categories"],
    }),
    addSubSubCategory: build.mutation({
      query: ({ categoryId, subId, ...body }) => ({ url: `category_routes/${categoryId}/sub/${subId}/subsub`, method: "POST", body }),
      invalidatesTags: ["Categories"],
    }),

    // ── Brands ────────────────────────────────────────────────────────────
    getBrands: build.query({
      query: ({ search = "" } = {}) => ({ url: "brand_routes", params: { search } }),
      providesTags: ["Brands"],
    }),
    createBrand: build.mutation({
      query: (body) => ({ url: "brand_routes", method: "POST", body }),
      invalidatesTags: ["Brands"],
    }),
    updateBrand: build.mutation({
      query: ({ id, ...body }) => ({ url: `brand_routes/${id}`, method: "PUT", body }),
      invalidatesTags: ["Brands"],
    }),
    deleteBrand: build.mutation({
      query: (id) => ({ url: `brand_routes/${id}`, method: "DELETE" }),
      invalidatesTags: ["Brands"],
    }),

    // ── Refunds ───────────────────────────────────────────────────────────
    getRefunds: build.query({
      query: ({ status = "", search = "" } = {}) => ({
        url: "refund_routes",
        params: { status, search },
      }),
      providesTags: ["Refunds"],
    }),
    createRefund: build.mutation({
      query: (body) => ({ url: "refund_routes", method: "POST", body }),
      invalidatesTags: ["Refunds"],
    }),
    updateRefundStatus: build.mutation({
      query: ({ id, status }) => ({ url: `refund_routes/${id}/status`, method: "PUT", body: { status } }),
      invalidatesTags: ["Refunds"],
    }),
    deleteRefund: build.mutation({
      query: (id) => ({ url: `refund_routes/${id}`, method: "DELETE" }),
      invalidatesTags: ["Refunds"],
    }),

    // ── Users (team / admin management) ───────────────────────────────────
    getUsers: build.query({
      query: ({ role = "" } = {}) => ({ url: "user_routes", params: role ? { role } : {} }),
      providesTags: ["Admins"],
    }),
    updateUser: build.mutation({
      query: ({ id, ...body }) => ({ url: `user_routes/${id}`, method: "PUT", body }),
      invalidatesTags: ["Admins"],
    }),
    deleteUser: build.mutation({
      query: (id) => ({ url: `user_routes/${id}`, method: "DELETE" }),
      invalidatesTags: ["Admins"],
    }),

    // ── Notifications ─────────────────────────────────────────────────────
    getNotifications: build.query({
      query: ({ search = "", status = "" } = {}) => ({ url: "notification_routes", params: { search, status } }),
      providesTags: ["Notifications"],
    }),
    createNotification: build.mutation({
      query: (body) => ({ url: "notification_routes", method: "POST", body }),
      invalidatesTags: ["Notifications"],
    }),
    updateNotification: build.mutation({
      query: ({ id, ...body }) => ({ url: `notification_routes/${id}`, method: "PUT", body }),
      invalidatesTags: ["Notifications"],
    }),
    deleteNotification: build.mutation({
      query: (id) => ({ url: `notification_routes/${id}`, method: "DELETE" }),
      invalidatesTags: ["Notifications"],
    }),

    // ── Support tickets ───────────────────────────────────────────────────
    getTickets: build.query({
      query: ({ priority = "", status = "", search = "" } = {}) => ({
        url: "support_routes",
        params: { priority, status, search },
      }),
      providesTags: ["Tickets"],
    }),
    getTicket: build.query({
      query: (id) => `support_routes/${id}`,
      providesTags: ["Tickets"],
    }),
    createTicket: build.mutation({
      query: (body) => ({ url: "support_routes", method: "POST", body }),
      invalidatesTags: ["Tickets"],
    }),
    replyTicket: build.mutation({
      query: ({ id, ...body }) => ({ url: `support_routes/${id}/reply`, method: "POST", body }),
      invalidatesTags: ["Tickets"],
    }),
    updateTicketStatus: build.mutation({
      query: ({ id, status }) => ({ url: `support_routes/${id}/status`, method: "PUT", body: { status } }),
      invalidatesTags: ["Tickets"],
    }),

    // ── Conversations (inbox / messaging) ─────────────────────────────────
    getConversations: build.query({
      query: ({ search = "" } = {}) => ({ url: "conversation_routes", params: { search } }),
      providesTags: ["Conversations"],
    }),
    getConversation: build.query({
      query: (id) => `conversation_routes/${id}`,
      providesTags: ["Conversations"],
    }),
    createConversation: build.mutation({
      query: (body) => ({ url: "conversation_routes", method: "POST", body }),
      invalidatesTags: ["Conversations"],
    }),
    sendMessage: build.mutation({
      query: ({ id, ...body }) => ({ url: `conversation_routes/${id}/messages`, method: "POST", body }),
      invalidatesTags: ["Conversations"],
    }),

    // ── Bulk product import ───────────────────────────────────────────────
    bulkImportProducts: build.mutation({
      query: (products) => ({ url: "product_routes/bulk", method: "POST", body: { products } }),
      invalidatesTags: ["Products"],
    }),

    // ── Settings ──────────────────────────────────────────────────────────
    getSettings: build.query({
      query: () => "settings",
      providesTags: ["Settings"],
    }),
    updateSettings: build.mutation({
      query: (body) => ({ url: "settings", method: "PUT", body }),
      invalidatesTags: ["Settings"],
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetProductsQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useGetLowStockProductsQuery,
  useUpdateStockMutation,
  useGetOrdersQuery,
  useGetOrderQuery,
  useCreateOrderMutation,
  useUpdateOrderStatusMutation,
  useProcessPaymentMutation,
  useCancelOrderMutation,
  useGetCustomersQuery,
  useGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useGetSuppliersQuery,
  useGetSupplierQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
  useDeleteSupplierMutation,
  useGetDashboardQuery,
  useGetSalesQuery,
  useGetInventoryAnalyticsQuery,
  useGetCustomerAnalyticsQuery,
  useGetGeographyQuery,
  useGetFinancialAnalyticsQuery,
  useGetCategoryAnalyticsQuery,
  useGetPaymentAnalyticsQuery,
  useGetSupplierAnalyticsQuery,
  useGetTransactionsQuery,
  useGetAdminsQuery,
  useGetUserPerformanceQuery,
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetSubCategoriesQuery,
  useAddSubCategoryMutation,
  useDeleteSubCategoryMutation,
  useAddSubSubCategoryMutation,
  useGetBrandsQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useDeleteBrandMutation,
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useGetRefundsQuery,
  useCreateRefundMutation,
  useUpdateRefundStatusMutation,
  useDeleteRefundMutation,
  useGetUsersQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetNotificationsQuery,
  useCreateNotificationMutation,
  useUpdateNotificationMutation,
  useDeleteNotificationMutation,
  useGetTicketsQuery,
  useGetTicketQuery,
  useCreateTicketMutation,
  useReplyTicketMutation,
  useUpdateTicketStatusMutation,
  useGetConversationsQuery,
  useGetConversationQuery,
  useCreateConversationMutation,
  useSendMessageMutation,
  useBulkImportProductsMutation,
} = api;
